import EmberService, { service } from '@ember/service';

import type { Editor as TinymceEditor, Ui } from 'tinymce';

import type { Service } from 'webapp-types';
import ThemeConfig from 'webapp/const/theme-config';
import { DataStoryType, StoryElementType } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';

import type { CompatibleStoryModel } from 'webapp/models-custom/story-element/base';
import type StoryElementBase from 'webapp/models-custom/story-element/base';
import StoryElementCompareChange from 'webapp/models-custom/story-element/compare-change';
import StoryElementCompareGranulationLabel from 'webapp/models-custom/story-element/compare-granulation-label';
import StoryElementCompareType from 'webapp/models-custom/story-element/compare-type';
import StoryElementCompareValue from 'webapp/models-custom/story-element/compare-value';
import StoryElementDateRange from 'webapp/models-custom/story-element/date-range';
import StoryElementGranulationLabel from 'webapp/models-custom/story-element/granulation-label';
import StoryElementMetric from 'webapp/models-custom/story-element/metric';
import StoryElementValue from 'webapp/models-custom/story-element/value';

export default class DataStoryService extends EmberService {
	@service('constants') declare constantsService: Service.Constants;

	storyElementOrderInTinyMceDropdown = [
		StoryElementType.METRIC,
		StoryElementType.VALUE,
		StoryElementType.DATE_RANGE,
		StoryElementType.COMPARE_TYPE,
		StoryElementType.COMPARE_CHANGE,
		StoryElementType.COMPARE_VALUE,
	];

	defaultEditorOptions = {
		toolbar: [
			// array instead of string to add title for storybutton group - for styling
			{ name: 't1', items: ['fontselect', 'styleselect'] },
			{ name: 't2', items: ['forecolor', 'bold', 'italic', 'underline'] },
			{ name: 't3', items: ['alignleft', 'aligncenter', 'alignright'] },
			{
				name: 't4',
				items: ['bullist', 'numlist', 'outdent', 'indent'],
			},
			{ name: 't5', items: ['table', 'emoticons', 'removeformat', 'link', 'hr'] },
			{ name: 'toolbar__group--storybutton', items: ['storybutton'] },
		],

		// custom story stuff
		// this tells tinyMCE not to strip this element.
		// when prefixed with ~, the element displays inline.
		custom_elements: '~story-element,~ele',
		extended_valid_elements: 'story-element[*],span[*]', // span added for story-element to be more styleable
		sanitizeFn: this.sanitizeTextWithStoryElements,
	};

	createStoryElementForType(
		type: string,
		opts: { model: CompatibleStoryModel },
	): StoryElementBase | null {
		const aOpts = opts;
		switch (type) {
			case StoryElementType.METRIC:
				return new StoryElementMetric(aOpts);
			case StoryElementType.DATE_RANGE:
				return new StoryElementDateRange(aOpts);
			case StoryElementType.VALUE:
				return new StoryElementValue(aOpts);
			case StoryElementType.GRANULATION_LABEL:
				return new StoryElementGranulationLabel(aOpts);
			case StoryElementType.COMPARE_TYPE:
				return new StoryElementCompareType(aOpts);
			case StoryElementType.COMPARE_CHANGE:
				return new StoryElementCompareChange(aOpts);
			case StoryElementType.COMPARE_VALUE:
				return new StoryElementCompareValue(aOpts);
			case StoryElementType.COMPARE_GRANULATION_LABEL:
				return new StoryElementCompareGranulationLabel(aOpts);
		}
		return null;
	}

	getStoryElementTitle(type: StoryElementType) {
		const model = { block: null } as CompatibleStoryModel;
		const element = this.createStoryElementForType(type, { model });
		return element?.longTitle || type;
	}

	addStoryElementToEditor(editor: TinymceEditor, type: StoryElementType, order: number) {
		const text = this.wrapStoryElementsWithSpans(
			this.getStoryElementHtmlForTypeAndOrder(type, order),
		);
		// without the &nbsp; inserting in a new line does not work
		editor.insertContent('&nbsp;' + text);
	}

	prepareStoryHtmlForOrder(order: number, type: DataStoryType, skipCompare = false) {
		return this.wrapStoryElementsWithSpans(this.getStoryHtmlForOrder(order, type, skipCompare));
	}

	sanitizeTextWithStoryElements(text: string): string {
		return sanitize(text, {
			CUSTOM_ELEMENT_HANDLING: {
				tagNameCheck: (tagName: string) => ['story-element', 'ele'].includes(tagName),
			},
		});
	}

	getStoryElementHtmlForTypeAndOrder(type: StoryElementType, order: number): string {
		return `<story-element data-type="${type}" data-order="${order}"></story-element>`;
	}

	getStoryHtmlForOrder(order: number, storyType: DataStoryType, skipCompare = false): string {
		const metricElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.METRIC,
				order,
			),
			valueElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.VALUE,
				order,
			),
			dateRangeElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.DATE_RANGE,
				order,
			),
			granulationLabelElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.GRANULATION_LABEL,
				order,
			),
			compareTypeElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.COMPARE_TYPE,
				order,
			),
			compareChangeElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.COMPARE_CHANGE,
				order,
			),
			compareValueElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.COMPARE_VALUE,
				order,
			),
			compareGranulationLabelElementHtml = this.getStoryElementHtmlForTypeAndOrder(
				StoryElementType.COMPARE_GRANULATION_LABEL,
				order,
			);

		const metricSentence = `${dateRangeElementHtml}, we're at ${valueElementHtml} ${metricElementHtml}.`,
			comparisonSentence = `This is a ${compareChangeElementHtml} from the ${compareTypeElementHtml}, which was at ${compareValueElementHtml}.`;

		let text;
		switch (storyType) {
			case DataStoryType.POSITIVITY_GOOD:
				text = `We're doing great. ${metricSentence}`;
				if (!skipCompare) {
					text += ' ' + comparisonSentence;
				}
				text = `<p>${text}</p>`;
				break;
			case DataStoryType.POSITIVITY_OK:
				text = `We're doing okay. ${metricSentence}`;
				if (!skipCompare) {
					text += ' ' + comparisonSentence;
				}
				text = `<p>${text}</p>`;
				break;
			case DataStoryType.POSITIVITY_BAD:
				text = `Not the best results as we're at ${valueElementHtml} ${metricElementHtml} for ${dateRangeElementHtml}.`;
				if (!skipCompare) {
					text += ' ' + comparisonSentence;
				}
				text = `<p>${text}</p>`;
				break;
			case DataStoryType.FORECAST_CURRENT:
				text = `Looking ahead to ${dateRangeElementHtml}, your forecasted value is expected to be ${valueElementHtml}.`;
				if (!skipCompare) {
					text += `<br>This represents a ${compareChangeElementHtml} compared to the current value, with a total change of ${compareValueElementHtml}.`;
				}
				text = `<p>${text}</p>`;
				break;
			case DataStoryType.FORECAST_HISTORICAL_PAST:
				text = `Looking back at your forecast from ${compareGranulationLabelElementHtml}, your forecasted value for ${granulationLabelElementHtml} was ${valueElementHtml}.`;
				if (!skipCompare) {
					text += `<br>The actual value in that period was ${compareValueElementHtml} which is ${compareChangeElementHtml} compared to the forecast.`;
				}
				text = `<p>${text}</p>`;
				break;
			case DataStoryType.FORECAST_HISTORICAL_FUTURE:
				text = `Looking back at your forecast from ${compareGranulationLabelElementHtml}, your forecasted value for ${dateRangeElementHtml} was ${valueElementHtml}.`;
				if (!skipCompare) {
					text += `<br>The current forecasted value is ${compareValueElementHtml} which shows ${compareChangeElementHtml} in forecasted performance.`;
				}
				text = `<p>${text}</p>`;
				break;
			case DataStoryType.POSITIVITY_NEUTRAL:
			default:
				text = metricSentence;
				if (!skipCompare) {
					text += ' ' + comparisonSentence;
				}
				text = `<p>${text}</p>`;
				break;
		}

		return text;
	}

	// To be styleable in TinyMCE inline mode
	wrapStoryElementsWithSpans(text: string) {
		const parser = new DOMParser(),
			htmlDoc = parser.parseFromString(text, 'text/html'),
			storyElementNodes = htmlDoc.querySelectorAll('story-element');

		if (storyElementNodes.length) {
			storyElementNodes.forEach((el) => {
				const elParentNode = el.parentNode as HTMLElement;

				if (!elParentNode.classList.contains('story-element-wrapper')) {
					const wrapper = document.createElement('span');
					wrapper.classList.add('story-element-wrapper');
					elParentNode.insertBefore(wrapper, el);
					wrapper.appendChild(el);
				}
			});

			text = htmlDoc.body.innerHTML;
		}

		return text;
	}

	cleanupTextAndGetOrdersToKeep(text: string, ordersToKeep: number[] = []) {
		const template = document.createElement('template');

		// cleanup whitespace and wrap in case we have multiple nodes (e.g. "p"s)
		template.innerHTML = `<div>${text.trim()}</div>`;
		const node = template.content.firstChild as HTMLDivElement;

		if (node) {
			const storyElementNodes = node.querySelectorAll('story-element');
			Array.from(storyElementNodes).forEach((storyElementNode) => {
				storyElementNode.removeAttribute('class');
				storyElementNode.removeAttribute('data-title');
				storyElementNode.textContent = '';

				const order = storyElementNode.getAttribute('data-order');
				if (order) {
					ordersToKeep.push(+order);
				}

				// not only clean the node, but make sure the type attribute is before the order attribute
				// tinymce output can mix them up, and it matters when comparing strings
				// this way we avoid saving just bcos of mixed attributes
				storyElementNode.removeAttribute('data-order');
				storyElementNode.setAttribute('data-order', String(order));
			});
		}

		const cleanText = node.innerHTML;

		return {
			cleanText,
			ordersToKeep: uniq(ordersToKeep),
		};
	}

	addStoryButtonToEditor(
		editor: TinymceEditor,
		getButtonItemsFn: () => Ui.Menu.NestedMenuItemContents[],
		colors = ThemeConfig[0]!.colors,
	) {
		// circle icons for existing and add new stories (green, orange, red)
		const createCircleSvg = (color: string): string => {
			const circleR = 6,
				circleD = 2 * circleR;
			return `<svg height="${circleD}" width="${circleD}"><circle cx="${circleR}" cy="${circleR}" r="${circleR}" fill="${color}" /></svg>`;
		};

		colors.forEach((color: string, idx: number) => {
			editor.ui.registry.addIcon(`circle${idx}`, createCircleSvg(color));
		});

		editor.ui.registry.addIcon('circle-grey', createCircleSvg('#8e929b'));
		editor.ui.registry.addIcon('circle-green', createCircleSvg('#02b875'));
		editor.ui.registry.addIcon('circle-orange', createCircleSvg('#f78d1b'));
		editor.ui.registry.addIcon('circle-red', createCircleSvg('#f76868'));
		editor.ui.registry.addIcon(
			'plusGreen',
			'<svg width="12" height="12" viewBox="0 0 320 320"><path fill="#25a767" d="M160.864 0c-88.364 0-159.975 71.605-159.975 159.977 0 88.393 71.612 160.023 159.975 160.023 88.37 0 160.026-71.63 160.026-160.023 0-88.372-71.656-159.977-160.026-159.977zM232.627 178.742h-53.058v52.936c0 10.369-8.326 18.752-18.706 18.752-10.33 0-18.706-8.383-18.706-18.752v-52.936h-52.957c-10.367 0-18.699-8.408-18.699-18.727 0-10.369 8.332-18.714 18.699-18.714h52.957v-53.018c0-10.343 8.377-18.702 18.706-18.702 10.38 0 18.706 8.358 18.706 18.702v53.018h53.058c10.317 0 18.7 8.345 18.7 18.714-0 10.318-8.383 18.727-18.7 18.727z"></path></svg>',
		);

		// override default icon (temporary-placeholder) - https://app.asana.com/0/1138028810573038/1208518818414247/f
		editor.ui.registry.addIcon('temporary-placeholder', createCircleSvg('#8e929b'));

		// toolbar menu button
		editor.ui.registry.addMenuButton('storybutton', {
			text: 'Add Data story',
			fetch: (callback) => {
				const items = getButtonItemsFn();
				callback(items);
			},
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		'data-story': DataStoryService;
	}
}
